import { Link } from "@tanstack/react-router";
import { ArrowRight, ExternalLink, FlaskConical, ShieldCheck } from "lucide-react";
import { CLUSTERS, type ClusterKey } from "@/lib/clusters";
import { CATALOG } from "@/lib/ucp/catalog";
import { RESEARCH } from "@/lib/research";
import { getIngredientPage } from "@/lib/ingredientDetail";

/**
 * Conversion block placed near the end of every article: the topic-matched
 * TruWe formulation, its clinically studied actives with the exact delivery
 * form and dose, a peer-reviewed citation per active, and a buy CTA.
 *
 * All content is derived from the locked formulation + research sources, so it
 * can never drift from the label or invent claims.
 */

const SKU_BY_CLUSTER: Record<ClusterKey, "mag" | "liver" | "ova"> = {
  magnesium: "mag",
  "liver-gut": "liver",
  hormonal: "ova",
  festive: "mag",
};

const rupees = (paise: number) => `₹${Math.round(paise / 100).toLocaleString("en-IN")}`;

export function ClinicalProductPitch({
  cluster,
  maxIngredients = 6,
}: {
  cluster: ClusterKey;
  maxIngredients?: number;
}) {
  const c = CLUSTERS[cluster];
  const sku = CATALOG.find((p) => p.handle === c.productHandle);
  const tier = sku?.tiers.find((t) => t.days === 30) ?? sku?.tiers[0];
  const skuCode = SKU_BY_CLUSTER[cluster];

  const actives = RESEARCH.filter((r) => r.sku === skuCode)
    .map((r) => {
      const page = getIngredientPage(r.slug);
      const dose = page?.detail.doseByProduct.find(
        (d) => d.productHandle === c.productHandle,
      );
      return {
        slug: r.slug,
        name: r.name,
        form: page?.detail.form ?? null,
        dose: dose?.dose ?? null,
        summary: r.summary,
        studyCount: r.studyCount,
        citation: r.citations[0] ?? null,
      };
    })
    .filter((a) => a.dose)
    .slice(0, maxIngredients);

  return (
    <section
      aria-label={`${c.productTitle} formulation`}
      className="mt-12 overflow-hidden rounded-3xl border border-brand/20 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-6 md:p-9"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
        The formulation behind this article
      </p>
      <h2 className="mt-2 font-display text-2xl font-bold text-brand-deep md:text-3xl">
        {c.productTitle}
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-foreground/80 md:text-base">
        {c.productTagline}
      </p>

      {actives.length > 0 && (
        <div className="mt-6 space-y-3">
          {actives.map((a) => (
            <div
              key={a.slug}
              className="rounded-2xl border border-border bg-card/80 p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h3 className="font-display text-sm font-semibold text-brand-deep md:text-base">
                  {a.name}
                </h3>
                {a.dose && (
                  <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-[11px] font-semibold text-brand">
                    {a.dose}
                  </span>
                )}
              </div>
              {a.form && (
                <p className="mt-1 text-xs text-muted-foreground">Form used: {a.form}</p>
              )}
              <p className="mt-2 text-sm text-foreground/80">{a.summary}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <FlaskConical className="h-3.5 w-3.5 text-brand" />
                  {a.studyCount}
                </span>
                {a.citation && (
                  <a
                    href={a.citation.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1 text-brand underline-offset-2 hover:underline"
                  >
                    {a.citation.journal} ({a.citation.year})
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <Link
                  to="/ingredients/$slug"
                  params={{ slug: a.slug }}
                  className="text-muted-foreground underline-offset-2 hover:text-brand hover:underline"
                >
                  Full research
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Link
          to="/products/$handle"
          params={{ handle: c.productHandle }}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-primary-foreground shadow-luxury transition hover:bg-brand-deep"
        >
          Shop {c.productTitle}
          {tier ? ` — ${rupees(tier.pricePaise)}` : ""}
          <ArrowRight className="h-4 w-4" />
        </Link>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-brand" />
          Third-party tested · FSSAI / GMP facility
        </span>
      </div>

      <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
        Food supplement, not a medicine. Ingredient research describes individual
        actives and their delivery forms, not finished-product outcomes. Consult a
        clinician if pregnant, lactating or on prescription medication.
      </p>
    </section>
  );
}
